import React, { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon, Columns3Icon, LockIcon, RotateCcwIcon } from 'lucide-react';
import { iconFor } from '../table/columnIcons';
import { Checkbox } from '../ui/Checkbox';
import { Popover } from '../ui/Popover';

export function ColumnsMenu({ orderedKeys, visibleKeys, lockedKeys, meta, isCustomized, onToggle, onSelectAll, onDeselectAll, onResetLayout }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const allVisible = visibleKeys.length === orderedKeys.length;
  const noneVisible = visibleKeys.every(k => lockedKeys.includes(k));

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="tw-inline-flex tw-h-9 tw-shrink-0 tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-900 tw-transition-colors tw-duration-150 hover:tw-bg-sand-50">
        <Columns3Icon className="tw-h-4 tw-w-4 tw-text-neutral-500" aria-hidden />
        Columns
        <span className="tw-text-xs tw-tabular-nums tw-text-neutral-400">
          {visibleKeys.length}/{orderedKeys.length}
        </span>
        <ChevronDownIcon className="tw-h-4 tw-w-4 tw-opacity-70" aria-hidden />
      </button>
      <Popover anchorRef={anchorRef} open={open} onClose={close} align="end" label="Choose columns" width={300}>
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-neutral-200 tw-px-3 tw-py-2.5">
          <p className="tw-text-xs tw-font-medium tw-text-neutral-500">Visible columns</p>
          <div className="tw-flex tw-items-center tw-gap-1">
            <button
              type="button"
              onClick={onSelectAll}
              disabled={allVisible}
              className="tw-rounded-md tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-brand-600 tw-transition-colors hover:tw-bg-brand-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
              Select all
            </button>
            <button
              type="button"
              onClick={onDeselectAll}
              disabled={noneVisible}
              className="tw-rounded-md tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-neutral-500 tw-transition-colors hover:tw-bg-sand-50 hover:tw-text-neutral-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-40">
              Deselect all
            </button>
          </div>
        </div>
        <ul className="scrollbar-thin tw-max-h-80 tw-overflow-y-auto tw-p-1.5">
          {orderedKeys.map(key => {
            const locked = lockedKeys.includes(key);
            const Icon = iconFor(key);
            return (
              <li key={key}>
                <label className={`tw-flex tw-items-center tw-gap-2.5 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-sm tw-transition-colors tw-duration-100 ${locked ? 'tw-cursor-default' : 'tw-cursor-pointer hover:tw-bg-sand-50'}`}>
                  <Checkbox checked={visibleKeys.includes(key)} disabled={locked} onChange={() => onToggle(key)} />
                  <Icon className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-neutral-400" aria-hidden />
                  <span className="tw-flex-1 tw-truncate tw-text-neutral-900" title={meta[key]?.label}>{meta[key]?.label}</span>
                  {locked && <LockIcon className="tw-h-3.5 tw-w-3.5 tw-text-neutral-400" aria-label="Always shown" />}
                </label>
              </li>
            );
          })}
        </ul>
        <div className="tw-border-t tw-border-neutral-200 tw-px-3 tw-py-2.5">
          <p className="tw-text-xs tw-text-neutral-400">Drag headers to reorder · drag their edges to resize</p>
          {isCustomized && (
            <button type="button" onClick={onResetLayout} className="tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-brand-600 hover:tw-underline">
              <RotateCcwIcon className="tw-h-3 tw-w-3" aria-hidden />
              Reset order & widths
            </button>
          )}
        </div>
      </Popover>
    </>
  );
}
